import { useApi } from '../hooks/useApi.js';
import { fetchPartners } from '../lib/api.js';
import { SectionHead } from './SectionHead.jsx';

/**
 * Accreditation / partner logos as a single-row marquee that auto-scrolls
 * (right → left) and loops seamlessly. Hover to pause. Logos render larger
 * than the old grid; falls back to the partner name when no logo is set.
 *
 * Returns null if no partners are configured.
 */
export function PartnersStrip() {
  const { data: partners } = useApi(fetchPartners, []);
  const list = Array.isArray(partners) ? partners : [];
  if (list.length === 0) return null;

  // Pad to a comfortable length, then duplicate so the -50% loop is seamless
  // regardless of how many logos the school has.
  let base = [...list];
  while (base.length < 8) base = [...base, ...list];
  const track = [...base, ...base];

  return (
    <section>
      <div className="container">
        <SectionHead
          align="center"
          eyebrow="Affiliations &amp; partners"
          rawTitle="Recognised by the world's <em>leading</em> institutions"
          lede="From curriculum bodies to international universities — Maple Leaf is held to the same standards as the institutions our students go on to join."
        />
      </div>

      <div className="partners-marquee" aria-label="Partners and affiliations">
        <div className="partners-track">
          {track.map((p, i) => {
            const isDup = i >= base.length;
            // Only allow safe, absolute/relative links.
            const href = p.url && /^(https?:\/\/|\/)/i.test(p.url) ? p.url : null;
            const inner = p.logo
              ? <img src={p.logo} alt={isDup ? '' : p.name} loading="lazy" />
              : <span>{p.name}</span>;
            const key = `${p.id ?? p.name}-${i}`;
            return href ? (
              <a
                className="partner-logo"
                key={key}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                title={p.name}
                aria-hidden={isDup ? 'true' : undefined}
                tabIndex={isDup ? -1 : undefined}
              >
                {inner}
              </a>
            ) : (
              <div className="partner-logo" key={key} title={p.name} aria-hidden={isDup ? 'true' : undefined}>
                {inner}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
